import React, {useState, useCallback, useEffect} from 'react';
import {useNavigate, useLocation} from "react-router-dom";
import Box from '@mui/material/Box';
import Grid from "@mui/material/Grid";
import LockResetIcon from '@mui/icons-material/LockReset';
import Typography from "@mui/material/Typography";
import { useForm, SubmitHandler } from "react-hook-form";
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";
import {useTheme} from "@mui/material/styles";

import {
    Form,
    DaButton,
    DaPasswordField,
    ProgressPanel,
    MessagePanel,
    IMessagePanelProps,
    ButtonBar,
} from "../_shared";
import {LoginPaper} from "./Login.styles";
import {authenticationApi} from "../../_api/auth.api";

interface LocationState {
    session: string;
    username: string;
    email: string;
}

interface INewPasswordForm {
    new_password: string;
    confirm_password: string;
}

const newPasswordSchema: yup.SchemaOf<INewPasswordForm> = yup.object({
    new_password: yup.string()
        .label("New Password")
        .min(10, 'Password must be at least 10 characters long')
        .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
        .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
        .matches(/\d/, 'Password must contain at least one number')
        .matches(/[@\$!%\*\?&#<>\^\+=\|]/, 'Password must contain at least one special character (@$!%*?&#<>^+=|)')
        .required('Password is required'),
    confirm_password: yup.string()
        .label("Confirm Password")
        .oneOf([yup.ref("new_password"), null], "Passwords must match")
        .required('Please confirm your password'),
});

export const SetNewPasswordView: React.FC = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const locationState = location.state as LocationState;
    
    const [msgPanelProps, setMsgPanelProps] = useState<IMessagePanelProps>({message: ""});
    const [progressTitle, setProgressTitle] = useState<string>("Setting new password...");
    const [isShowError, setIsShowError] = useState<boolean>(false);
    const [isShowSuccess, setIsShowSuccess] = useState<boolean>(false);
    const [showProgress, setShowProgress] = useState<boolean>(false);

    const formFieldSize = "medium";
    const formFieldVariant = "standard";

    // Check if we have the required session data
    useEffect(() => {
        if (!locationState?.session || !locationState?.username || !locationState?.email) {
            // Redirect to login if no session data
            navigate("/login", {replace: true});
        }
    }, [locationState, navigate]);

    // defining the initial state for the form
    const initialValues: INewPasswordForm = {
        new_password: '',
        confirm_password: '',
    };

    const { control, handleSubmit, formState: { errors } } = useForm<INewPasswordForm>({
        defaultValues: initialValues,
        resolver: yupResolver(newPasswordSchema)
    });

    const showError = ({title, message, type="error"}: IMessagePanelProps) => {
        const msgProps: IMessagePanelProps = {
            message: message,
            title: title,
            type: type
        }
        setMsgPanelProps(msgProps);
        setIsShowError(true);
        setIsShowSuccess(false);
    }

    const showSuccess = ({title, message}: IMessagePanelProps) => {
        const msgProps: IMessagePanelProps = {
            message: message,
            title: title,
            type: "info"
        }
        setMsgPanelProps(msgProps);
        setIsShowSuccess(true);
        setIsShowError(false);
    }

    const onSubmit: SubmitHandler<INewPasswordForm> = (data) => {
        submitNewPassword(data);
    };

    const submitNewPassword = useCallback(async (formData: INewPasswordForm): Promise<void> => {
        if (!locationState?.session || !locationState?.username || !locationState?.email) {
            showError({
                title: "Session Error",
                message: "Session data is missing. Please try logging in again.",
            });
            return;
        }

        setProgressTitle("Setting new password...");
        setShowProgress(true);
        setIsShowError(false);

        try {
            const resp = await authenticationApi.set_new_password({
                username: locationState.username,
                email: locationState.email,
                new_password: formData.new_password,
                session: locationState.session,
            });

            if (resp && resp.success === true) {
                showSuccess({
                    title: "Success!",
                    message: "Your password has been set successfully. Redirecting to login...",
                });
                
                // Redirect to login after a short delay
                setTimeout(() => {
                    navigate("/login", {replace: true});
                }, 2000);
            } else {
                showError({
                    title: "Unable to Set Password",
                    message: resp?.message || "An error occurred while setting your new password. Please try again.",
                });
            }
        } catch (e: any) {
            showError({
                title: "API Error",
                message: "An error occurred while setting your new password. Please try again.",
            });
        } finally {
            setShowProgress(false);
        }
    }, [locationState, navigate]);

    // Don't render if no session data
    if (!locationState?.session || !locationState?.username || !locationState?.email) {
        return null;
    }

    return (
        <>
            <LoginPaper elevation={4}>
                <Box display="flex" flexDirection="column" alignItems="center">
                    <LockResetIcon
                        sx={{
                            fontSize: 68,
                            color: theme.palette.grey["500"],
                            mb: 2
                        }}
                    />
                    <Typography variant="h4">Set New Password</Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 1, textAlign: 'center' }}>
                        Please set a new password for your account.
                    </Typography>
                </Box>

                <Grid container>
                    {isShowError ? (
                        <Grid item xs={12} justifyContent={"start"}>
                            <MessagePanel {...msgPanelProps} />
                        </Grid>
                    ) : null}
                    {isShowSuccess ? (
                        <Grid item xs={12} justifyContent={"start"}>
                            <MessagePanel {...msgPanelProps} />
                        </Grid>
                    ) : null}
                    {showProgress ? (
                        <Grid item xs={12}>
                            <ProgressPanel title={progressTitle}/>
                        </Grid>
                    ) : (
                        <Grid container item xs={12}>
                            <Form
                                autoComplete="off"
                                onSubmit={handleSubmit(onSubmit)}
                            >
                                <Grid container spacing={theme.spacing(1)}>
                                    <Grid item xs={12} md={12}>
                                        <DaPasswordField
                                            control={control}
                                            name="new_password"
                                            label="New Password"
                                            required={true}
                                            variant={formFieldVariant}
                                            size={formFieldSize}
                                            errors={errors}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={12}>
                                        <DaPasswordField
                                            control={control}
                                            name="confirm_password"
                                            label="Confirm Password"
                                            required={true}
                                            variant={formFieldVariant}
                                            size={formFieldSize}
                                            errors={errors}
                                        />
                                    </Grid>
                                </Grid>
                                <Grid container spacing={theme.spacing(1)}>
                                    <Grid item xs={12} md={12}>
                                        <ButtonBar buttonAlignment="alignRight">
                                            <DaButton buttonStyle="primary" type="submit">
                                                Set Password
                                            </DaButton>
                                        </ButtonBar>
                                    </Grid>
                                </Grid>
                            </Form>
                        </Grid>
                    )}
                </Grid>
            </LoginPaper>
        </>
    );
}
